import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import CssBaseline from "@mui/material/CssBaseline";
import { ThemeProvider as MuiThemeProvider } from "@mui/material/styles";
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { createAppTheme } from "./theme.config.js";
const ThemeContext = createContext(undefined);
export const useThemeMode = () => {
    const context = useContext(ThemeContext);
    if (!context) {
        throw new Error("useThemeMode must be used within ThemeContextProvider");
    }
    return context;
};
export const ThemeContextProvider = ({ children }) => {
    // Initialize from localStorage or default to light
    const [mode, setMode] = useState(() => {
        const savedMode = localStorage.getItem("theme-mode");
        return savedMode || "light";
    });
    // Detect system theme preference
    const [systemPreference, setSystemPreference] = useState(() => {
        if (typeof window !== "undefined" && window.matchMedia) {
            return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
        }
        return "light";
    });
    // Listen for system theme changes
    useEffect(() => {
        if (typeof window === "undefined" || !window.matchMedia)
            return;
        const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
        const handleChange = (e) => {
            setSystemPreference(e.matches ? "dark" : "light");
        };
        mediaQuery.addEventListener("change", handleChange);
        return () => mediaQuery.removeEventListener("change", handleChange);
    }, []);
    // Persist to localStorage when mode changes
    useEffect(() => {
        localStorage.setItem("theme-mode", mode);
    }, [mode]);
    // Both callbacks are stable across renders, and the context value is
    // memoised on top of them. Identity matters here: consumers put these in
    // effect dependency arrays, and a fresh closure on every provider render
    // makes such an effect re-run on every theme change. `AppLayout` had
    // exactly that effect — it re-applied the server's saved `themeSetting`
    // each time the provider re-rendered, so a user toggling the theme was
    // snapped straight back to the stored value with nothing thrown.
    const setThemeMode = useCallback((newMode) => {
        setMode(newMode);
    }, []);
    const toggleTheme = useCallback(() => {
        setMode((prevMode) => {
            if (prevMode === "light")
                return "dark";
            if (prevMode === "dark")
                return "system";
            return "light";
        });
    }, []);
    // Determine actual theme to use
    const actualMode = mode === "system" ? systemPreference : mode;
    // Create theme based on mode using the centralized createAppTheme function
    // This preserves all component overrides, typography, and other customizations
    const theme = useMemo(() => createAppTheme(actualMode), [actualMode]);
    const value = useMemo(() => ({ actualMode, mode, setThemeMode, toggleTheme }), [actualMode, mode, setThemeMode, toggleTheme]);
    return (_jsx(ThemeContext.Provider, { value: value, children: _jsxs(MuiThemeProvider, { theme: theme, children: [_jsx(CssBaseline, {}), children] }) }));
};
//# sourceMappingURL=ThemeContext.js.map